/**
 * @ipc/validation · src/common/primitives.ts — общие zod-примитивы (раздел 13).
 * tiyinAmount/uuidSchema/phoneSchema/langSchema/paginationSchema дословно из
 * "Контракты и интерфейсы" tech-spec/13-api-validation.md.
 *
 * Канонические domain-enum'ы (PaymentStatus / RefundStatus / PaymentProvider /
 * ApiKeyStatus) — единый источник для API-схем и admin-ui; значения совпадают
 * с pgEnum в `@ipc/db` (раздел 02) и `@ipc/core` status-автоматом.
 */
import { z } from 'zod/v4';
import { PaymentStatus, PAYMENT_STATUSES } from '@ipc/core';

/** Ре-экспорт const-объекта и массива значений из `@ipc/core` (единый SSOT). */
export { PaymentStatus, PAYMENT_STATUSES };

/** Сумма в тийинах (1 сум = 100 тийин). Всегда целое, неотрицательное. */
export const tiyinAmount = z
  .number()
  .int('Сумма должна быть целым числом тийин')
  .nonnegative('Сумма не может быть отрицательной');

/** UUID транзакции Multicard. */
export const uuidSchema = z.string().uuid('Некорректный UUID');

/** Телефон в формате 998XXXXXXXXX (для sms-ссылки инвойса). */
export const phoneSchema = z
  .string()
  .regex(/^998\d{9}$/, 'Телефон в формате 998XXXXXXXXX');

/** Язык страницы оплаты Multicard. */
export const langSchema = z.enum(['ru', 'uz', 'en']);

/** Пагинация (query). coerce — query всегда строки. */
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type PaginationDto = z.infer<typeof paginationSchema>;

/** Полнотекстовый поиск (query). Пустая строка отбрасывается. */
export const searchSchema = z.string().trim().min(1).optional();

/** Направление сортировки (query). Дефолт — desc (свежие сверху). */
export const sortOrderSchema = z.enum(['asc', 'desc']).default('desc');
export type SortOrder = z.infer<typeof sortOrderSchema>;

/**
 * Boolean-фильтр из query-строки. НЕ использовать z.coerce.boolean() —
 * Boolean('false') === true, из-за чего фильтр «false» инвертируется.
 * Принимает строки 'true'/'false' (и нативные booleans), отбрасывает пустое.
 */
export const booleanQuerySchema = z
  .enum(['true', 'false'])
  .transform((v) => v === 'true')
  .optional();

/**
 * Нейтральный статус-автомат ядра (раздел 03/канон, `@ipc/core` `PaymentStatus`,
 * литеральный `as const`-объект — сохраняет строгую union-типизацию значений,
 * в отличие от `PAYMENT_STATUSES: readonly PaymentStatus[]`, которая для
 * `z.enum()` виднеется до `string[]`), НЕ путать с сырым lowercase-enum'ом
 * Multicard (`enums/multicard.ts` `paymentStatusEnum`:
 * draft/progress/billing/hold/success/error/revert — существует ТОЛЬКО внутри
 * драйвера). Баг обнаружен HTTP-E2E гейтом: `schemas/invoices.ts` раньше
 * ошибочно использовал сырой Multicard-enum для ответа `POST /api/v1/invoices`,
 * который несёт нейтральный `PaymentStatus` (`status: 'CREATED'`) — валит КАЖДЫЙ
 * вызов эндпоинта 500 (zod отвергает `'CREATED'`, нет в семи сырых значениях).
 */
export const paymentStatusSchema = z.enum(PaymentStatus);
/** Union-тип нейтрального статуса платежа (тот же, что `@ipc/core` PaymentStatus). */
export type PaymentStatusValue = z.infer<typeof paymentStatusSchema>;

/**
 * Статусы возврата (канон 02, `RefundStatus` / pgEnum `@ipc/db`).
 * Отдельный enum от PaymentStatus — не смешивать.
 */
export const REFUND_STATUSES = ['REQUESTED', 'PROCESSING', 'SUCCESS', 'FAILED'] as const;
export const refundStatusSchema = z.enum(REFUND_STATUSES);
export type RefundStatus = z.infer<typeof refundStatusSchema>;

/**
 * Провайдер платёжного шлюза (канон 02, `PaymentProvider` / pgEnum `@ipc/db`).
 * Uppercase-коды API/БД/UI; не путать с `@ipc/core` `ProviderCode` (lowercase
 * ключ драйвера, напр. `'multicard'`).
 */
export const PAYMENT_PROVIDERS = ['MULTICARD', 'PAYME', 'CLICK', 'CLOUDPAYMENTS'] as const;
export const paymentProviderSchema = z.enum(PAYMENT_PROVIDERS);
export type PaymentProviderCode = z.infer<typeof paymentProviderSchema>;

/** Статус API-ключа (канон 02, `ApiKeyStatus` / pgEnum `@ipc/db`). */
export const API_KEY_STATUSES = ['ACTIVE', 'REVOKED'] as const;
export const apiKeyStatusSchema = z.enum(API_KEY_STATUSES);
export type ApiKeyStatus = z.infer<typeof apiKeyStatusSchema>;
